import React, { useEffect } from 'react';
import { useLandlordStore } from '@/stores/useLandlordStore';
import { Calendar, Check, X, Clock, HelpCircle, ArrowLeft, Moon } from 'lucide-react';
import { LoadingScreen } from '@/components/common/LoadingSpinner';
import { useNavigate } from 'react-router-dom';

export const LandlordBookings: React.FC = () => {
    const { bookings, fetchBookings, updateBookingStatus, loading, error } = useLandlordStore();
    const navigate = useNavigate();

    useEffect(() => {
        fetchBookings();
    }, []);

    const getStatusStyle = (status: string) => {
        switch (status) {
            case "Pending":
                return {
                    icon: Clock,
                    colorClass: "text-primary dark:text-golden",
                    bgClass: "bg-primary/20 dark:bg-golden/20",
                    borderClass: "border-l-primary dark:border-l-golden"
                };
            case "Confirmed":
                return {
                    icon: Check,
                    colorClass: "text-green-600 dark:text-green-400",
                    bgClass: "bg-green-100 dark:bg-green-900/30",
                    borderClass: "border-l-green-600 dark:border-l-green-400"
                };
            case "Cancelled":
                return {
                    icon: X,
                    colorClass: "text-red-600 dark:text-red-400",
                    bgClass: "bg-red-100 dark:bg-red-900/30",
                    borderClass: "border-l-red-600 dark:border-l-red-400"
                };
            default:
                return {
                    icon: HelpCircle,
                    colorClass: "text-light-subtle dark:text-dark-subtle",
                    bgClass: "bg-gray-100 dark:bg-gray-800",
                    borderClass: "border-l-gray-400 dark:border-l-gray-600"
                };
        }
    };

    if (loading && bookings.length === 0) return <LoadingScreen />;

    return (
        <div className="space-y-6 pb-12 min-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center gap-4 bg-light-card dark:bg-dark-card p-4 rounded-lg shadow-sm border border-light-border dark:border-dark-border">
                <button
                    onClick={() => navigate(-1)}
                    className="p-2 rounded-full hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
                >
                    <ArrowLeft size={24} className="text-primary dark:text-golden" />
                </button>
                <h1 className="text-xl font-bold text-light-text dark:text-dark-text">Viewing Requests</h1>
            </div>

            {error ? (
                <div className="flex flex-col flex-1 items-center justify-center p-8 text-center text-red-600 dark:text-red-400">
                    <p className="mb-4">{error}</p>
                    <button 
                        onClick={() => fetchBookings()} 
                        className="font-bold text-primary dark:text-golden hover:underline"
                    >
                        Try Again
                    </button>
                </div>
            ) : bookings.length === 0 ? (
                 <div className="flex flex-col flex-1 items-center justify-center p-12 text-center text-light-subtle dark:text-dark-subtle">
                    <Moon size={60} className="mb-4 opacity-70" />
                    <p className="text-lg max-w-md">No viewing requests yet. When a tenant requests a viewing, it will appear here.</p>
                </div>
            ) : (
                <div className="grid gap-4">
                    {bookings.map((booking) => {
                        const style = getStatusStyle(booking.status);
                        const StatusIcon = style.icon;
                        
                        return (
                            <div 
                                key={booking.id} 
                                className={`bg-light-card dark:bg-dark-card rounded-xl p-5 shadow-sm border border-light-border dark:border-dark-border border-l-4 ${style.borderClass}`}
                            >
                                <div className="flex justify-between items-start mb-3">
                                    <h3 className="text-lg font-bold text-light-text dark:text-dark-text leading-tight flex-1 mr-4">
                                        {booking.unitName} at {booking.propertyName}
                                    </h3>
                                    <div className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold ${style.bgClass} ${style.colorClass}`}>
                                        <StatusIcon size={14} />
                                        <span>{booking.status}</span>
                                    </div>
                                </div>
                                
                                <div className="flex items-center gap-3 py-3 text-light-text dark:text-dark-text">
                                    <img 
                                        src={booking.tenantAvatar || "https://i.pravatar.cc/150?u=default"} 
                                        alt={booking.tenantName} 
                                        className="w-8 h-8 rounded-full border border-light-border dark:border-dark-border"
                                    />
                                    <span className="text-sm font-medium">Requested by: {booking.tenantName}</span>
                                </div>
                                
                                <div className="pt-3 border-t border-light-border dark:border-dark-border flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                                    <div className="flex items-center gap-2 text-primary dark:text-golden font-semibold text-sm">
                                        <Calendar size={18} className="text-light-subtle dark:text-dark-subtle" />
                                        <span>{booking.requestedDate} at {booking.requestedTime}</span>
                                    </div>
                                    
                                    {booking.status === 'Pending' && (
                                        <div className="flex gap-2 w-full sm:w-auto mt-2 sm:mt-0">
                                            <button 
                                                onClick={() => updateBookingStatus(booking.id, 'Confirmed')}
                                                className="flex-1 sm:flex-none flex items-center justify-center gap-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-semibold"
                                            >
                                                <Check size={16} /> Confirm
                                            </button>
                                            <button 
                                                onClick={() => updateBookingStatus(booking.id, 'Cancelled')}
                                                className="flex-1 sm:flex-none flex items-center justify-center gap-1 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm font-semibold"
                                            >
                                                <X size={16} /> Decline
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
